import type { IdbFailure, IdbFailureKind } from "./_types";

/**
 * 저장소 오류를 degrade 범위로 바꾸는 분류기.
 *
 * Owns:
 * - `DOMException` 이름에서 `IdbFailureKind` 로의 대응.
 * - 어떤 종류가 얼마나 오래 저장소를 닫는지(`degradeScopeFor`), 무엇을 보고할지의 정책.
 *
 * Does not own:
 * - degrade 상태의 보관(`public` 의 `IdbCache`).
 * - 보고 방식(주입된 reporter).
 *
 * 분류의 목적은 호출부의 분기가 아니다. 캐시 소비처는 성공 여부만 본다. 분류의 목적은
 * **얼마나 오래 포기할지**를 정하는 것이다. 용량이 찼거나 디스크를 신뢰할 수 없게 된 뒤에도
 * 화면마다 같은 시도를 반복하면, 실패 비용(대량 structured clone)을 계속 쓴다.
 */

/** 실패가 닫는 범위. `session` 은 다음 `init()`(새로고침, 재로그인)까지 유지된다. */
export type IdbDegradeScope = "none" | "writes" | "session";

export function classifyIdbError(error: unknown): IdbFailure {
  if (!(error instanceof DOMException)) {
    return { kind: "unknown", message: describe(error) };
  }
  return { kind: kindForDomException(error.name), message: `${error.name}: ${error.message}` };
}

export function degradeScopeFor(kind: IdbFailureKind): IdbDegradeScope {
  switch (kind) {
    // 열 수 없는 저장소는 다음 op 에서도 열리지 않는다. 대표적인 경우는 다른 탭이 구버전 연결을
    // 유지하는 경우다. 이 탭이 열려 있는 동안 그 상태가 자동으로 풀린다는 근거가 없다.
    case "unavailable":
    case "corrupt":
      return "session";
    // 용량이 차도 이미 저장된 값을 읽는 캐시 적중은 여전히 쓸모가 있다.
    case "quota":
      return "writes";
    // 정상 종료(dispose·다른 탭의 versionchange)와 일회성 오류는 저장소를 닫지 않는다.
    case "aborted":
    case "unknown":
      return "none";
  }
}

/** 정상 종료 경로는 보고하지 않는다. 탭 이동이나 로그아웃마다 보고가 쌓이면 실제 실패가 묻힌다. */
export function isReportableFailure(kind: IdbFailureKind): boolean {
  return kind !== "aborted";
}

function kindForDomException(name: string): IdbFailureKind {
  switch (name) {
    case "QuotaExceededError":
      return "quota";
    // 접근할 수 없는 저장소의 세 경우다.
    //   - VersionError: 저장된 버전이 이 번들의 선언보다 높다. 이 탭이 구버전이다. 강제
    //     다운그레이드는 새 탭의 캐시를 망가뜨리므로 하지 않는다.
    //   - SecurityError: 사생활 보호 모드 또는 정책 차단.
    //   - TimeoutError: 다른 탭이 연결을 유지해서 기다려도 풀리지 않는다.
    case "VersionError":
    case "SecurityError":
    case "TimeoutError":
      return "unavailable";
    // UnknownError 는 Safari·Firefox 가 디스크 압박에서 던지는 이름이다. InvalidStateError 는
    // 축출 뒤 끊긴 연결을 다시 쓸 때 발생한다. 둘 다 저장소를 신뢰할 수 없다는 뜻이다.
    case "UnknownError":
    case "InvalidStateError":
      return "corrupt";
    case "AbortError":
      return "aborted";
    // NotFoundError(없는 스토어)·DataCloneError(복제할 수 없는 값)·TransactionInactiveError 는
    // 저장소 상태가 아니라 이 앱 코드의 오류다. 저장소를 닫지 않고 그 호출만 실패한다.
    default:
      return "unknown";
  }
}

function describe(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return typeof error === "string" && error !== "" ? error : "Unknown IndexedDB failure.";
}
